import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, getApiKey } from "../api/client";
import { format, addDays, parseISO } from "date-fns";
import { ImageIcon } from "lucide-react";

interface MediaAsset {
  id: number;
  entityType: string;
  entityId: number;
  platformCode: string | null;
  assetKind: string | null;
  fileSizeBytes: number | null;
  aspectRatio: string | null;
  remotePath: string | null;
  publicUrl: string | null;
  generationStatus: string;
  generationProvider: string | null;
  createdAt: string;
}

const KINDS: { value: string; label: string; description: string; aspect: string }[] = [
  { value: "dzen_cover",  label: "Дзен cover",       description: "обложка статьи 16:9",    aspect: "aspect-[16/9]" },
  { value: "feed_master", label: "Telegram / VK / MAX", description: "feed master 4:5",     aspect: "aspect-[4/5]" },
  { value: "ok_wide",     label: "Одноклассники",    description: "квадрат 1:1",            aspect: "aspect-square" },
  { value: "video_horizontal", label: "Ролик 16:9",   description: "лента ВК/ОК, Дзен-Видео", aspect: "aspect-[16/9]" },
  { value: "video_vertical",   label: "Ролик 9:16",   description: "Клипы, Shorts, Дзен-Ролики", aspect: "aspect-[9/16]" },
  { value: "video_story",      label: "История 15 с", description: "сторис ВК со ссылкой",       aspect: "aspect-[9/16]" },
];

/** Видео нельзя гнать через /webp — он прогоняет байты ресайзером картинок. */
const isVideoKind = (kind: string) => kind.startsWith("video_");

export function MediaGallery() {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [kind, setKind] = useState<string>("dzen_cover");

  const items = useQuery({
    queryKey: ["media", date, kind],
    queryFn: () => api<MediaAsset[]>(`/api/media?date=${date}&kind=${kind}`),
    retry: false,
  });

  const shift = (days: number) =>
    setDate(format(addDays(parseISO(date), days), "yyyy-MM-dd"));
  const kindMeta = KINDS.find(k => k.value === kind)!;
  const apiKey = getApiKey();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold">Картинки</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="btn-secondary">←</button>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
          <button onClick={() => shift(1)} className="btn-secondary">→</button>
          <button onClick={() => setDate(format(new Date(), "yyyy-MM-dd"))} className="btn-secondary">сегодня</button>
        </div>
      </div>

      {/* Сегмент-выбор kind с описанием */}
      <div className="card p-3 flex items-center gap-2 flex-wrap">
        {KINDS.map(k => (
          <button
            key={k.value}
            onClick={() => setKind(k.value)}
            className={`px-4 py-2 rounded-lg text-sm transition border ${
              kind === k.value
                ? "bg-astro-violet/20 border-astro-violet/50 text-white"
                : "bg-black/20 border-white/10 text-white/60 hover:text-white hover:bg-white/5"
            }`}
          >
            <div className="font-medium">{k.label}</div>
            <div className="text-xs opacity-70">{k.description}</div>
          </button>
        ))}
      </div>

      {items.isLoading && <p className="text-white/40 text-sm">Загружаем…</p>}
      {items.error && (
        <div className="card p-4 bg-rose-500/10 border-rose-500/30 text-rose-200 text-sm">
          Ошибка: {String((items.error as Error).message)}
        </div>
      )}

      {items.data && items.data.length === 0 && !items.isLoading && (
        <p className="text-white/40 text-sm">Картинок этого вида на эту дату нет.</p>
      )}

      {items.data && items.data.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.data.map((m) => {
            // Превью через worker proxy. API key прикладываем как query — proxy эндпоинт
            // защищён ApiKeyMiddleware, а image-теги не умеют слать заголовки.
            // WebP-превью: 480px шириной, q=78 — для тайла 300-400px на экране достаточно,
            // выходной файл ~5-15 KB вместо 5 MB исходного PNG. Загрузка галереи ускоряется в десятки раз.
            const previewUrl = `/api/media/${m.id}/webp?w=480&q=78${apiKey ? `&apiKey=${apiKey}` : ""}`;
            const fileName = m.remotePath?.split("/").pop() ?? `media #${m.id}`;
            return (
              <div key={m.id} className="card overflow-hidden">
                <div className={`${kindMeta.aspect} bg-black/40 flex items-center justify-center relative`}>
                  {isVideoKind(kind) ? (
                    <video
                      src={`/api/media/${m.id}/file${apiKey ? `?apiKey=${apiKey}` : ""}`}
                      controls
                      preload="metadata"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                  <img
                    src={previewUrl}
                    alt={fileName}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = "none";
                      target.parentElement?.querySelector("[data-fallback]")?.classList.remove("hidden");
                    }}
                  />
                  )}
                  <div data-fallback className="hidden absolute inset-0 flex items-center justify-center text-white/30">
                    <ImageIcon size={32} />
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-xs text-white/60 font-mono truncate" title={fileName}>{fileName}</div>
                  <div className="flex items-center justify-between mt-2 text-xs">
                    <span className="text-white/40">{m.aspectRatio ?? "?"}</span>
                    <span className="text-white/40">
                      {m.fileSizeBytes ? `${(m.fileSizeBytes / 1024).toFixed(0)} KB` : "?"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
